import { roomInactiveColor } from "./roomColors"

// `roomInactiveColor` is the guide's base accent (deep blue) used whenever color-coding is not
// "byRoom". The accent slots below reference it so the base look is defined in one place.
export const defaultProgramGuideConfig = {
    // Large headings above the columns: the active-filter/context summary and the per-day
    // labels ("Monday, Nov 2"). Positional margins (mt) stay inline at the call site.
    sectionHeading: { fontSize: "26px", fontWeight: 500, color: "gray.800", mb: 4 },
    // Container that wraps the day/track/dropdown filters + search at the top of the guide.
    // Border matches the neutral list scaffolding (gray.500), knocked back 50% so it reads
    // as a soft frame rather than a hard rule.
    filterPanel: {
        py: 6,
        // Tighter side padding on phones so the pills/search have more room to breathe.
        px: { base: 3, md: 6 },
        borderRadius: "10px",
        borderWidth: "0",
        borderColor: "gray.500",
        // Very light tint of the same cool gray; the pills/dropdowns/search sit on top as white.
        bg: "gray.500/5",
        // Consistent breathing room below the filters, so the results/day headings never butt
        // up against the box — including when no filters are active (no context heading above).
        mb: 8,
    },
    dayPills: {
        container: { gap: 2, mb: 3 },
        pill: { size: "md", borderRadius: "full", boxShadow: "sm", px: 4, fontSize: "md" },
        pillActive: { variant: "solid", fontWeight: 600 },
        pillInactive: { variant: "outline", fontWeight: 400, bg: "white" },
    },
    filterDropdowns: {
        container: { gap: 3, mb: 4 },
        trigger: {
            size: "md",
            borderRadius: "full",
            fontSize: "md",
            px: 4,
            minW: "168px",
            boxShadow: "sm",
            bg: "white",
        },
        badge: { bg: "black", color: "white" },
        dropdown: { borderColor: "gray.200", borderRadius: "md", boxShadow: "md", bg: "white" },
        checkboxColorPalette: "blue",
    },
    sessionList: {
        // Group + time headers span multiple rooms, so they stay a neutral gray (matching the
        // card's time/breadcrumb meta) rather than any single room's highlight color.
        timeLabel: { fontSize: "15px", fontWeight: "500", color: "gray.500", mt: 4, mb: 1 },
        divider: { bg: "gray.200" },
        groupHeader: { fontSize: "18px", fontWeight: "400", color: "gray.500", textTransform: "uppercase" },
        groupHeaderDivider: { bg: "gray.200" },
    },
    sessionCard: {
        container: { mb: 4, p: 4, borderRadius: 8, borderWidth: 1, _hover: {} },
        // Base selection treatment (blue accent), used when color-coding is not "byRoom". When it
        // is, SessionCard overrides these with the room color + a thicker 8px left accent.
        containerSelected: { borderColor: roomInactiveColor, borderLeftWidth: "6px", pl: 4 },
        containerDefault: {},
        tag: { bg: roomInactiveColor, alignSelf: "flex-start", borderRadius: "full" },
        title: { fontSize: "22px", fontWeight: 500, mt: 0 },
        meta: { fontSize: "12px", color: "gray.500" },
        breadcrumbs: { fontSize: "12px", color: "gray.500" },
        summary: { fontSize: "15px", color: "gray.600" },
        viewDetails: { fontSize: "14px", color: roomInactiveColor },
    },
    sessionDetail: {
        // accentColor drives: container border, tag bg, title color, section label colors.
        // Override individual slots to decouple from accentColor if needed.
        accentColor: roomInactiveColor,
        container: { p: 4, bg: "white", borderRadius: "md" },
        title: { fontSize: "24px", fontWeight: 500 },
        meta: { fontSize: "14px", color: "gray.500" },
        sectionLabel: { fontSize: "17px", fontWeight: "500" },
        // Track tags: neutral gray (matching the scaffolding). Topic tags: the base brand accent
        // (deep blue), so they stay visually distinct from the color-coded track tags.
        trackTag: { bg: "gray.500" },
        topicTag: { bg: roomInactiveColor },
        presentationDivider: { borderColor: "gray.100" },
        presentationTime: { fontSize: "13px", color: "gray.500" },
        presentationTitle: { fontSize: "15px", fontWeight: 500, color: "gray.600", lineHeight: "1.3" },
        // Card drawn behind a presentation row for a few seconds after its drawer closes, so the
        // reader can find where they were in the list. Applied to a pseudo-element sized to give
        // the text 10px of visual padding, so any bg / border / borderRadius works here without
        // moving the row's text. Twice the tint of the filter panel at the top of the guide.
        presentationHighlight: { bg: "gray.500/10", borderRadius: "5px" },
    },
}

// Conference overrides — define only the slots that differ from defaults.
// Use Chakra theme tokens (color1–color16) to reference the CMS-driven palette.
//
// Example:
// export const myConferenceTheme = {
//     sessionCard: {
//         containerSelected: { borderColor: "color1", borderWidth: "2px", borderLeftWidth: "4px" },
//         tag:               { bg: "color1", borderRadius: "sm" },
//         viewDetails:       { fontSize: "14px", color: "color1" },
//     },
//     sessionDetail: {
//         accentColor:  "color1",
//         title:        { fontSize: "24px", fontWeight: 700, fontFamily: "kallisto, sans-serif" },
//     },
// }

// Map siteId → override object. Add one entry per conference that needs a theme.
const themeMap = {}

export function resolveProgramGuideTheme(site) {
    return themeMap[site?.meeting?.siteId] ?? {}
}
